import React, { useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  ComposedChart
} from 'recharts';
import { chartColors } from "../theme/palette";

const Graphs = ({ selected, apiData = [], daysCount = 30, onDaysChange, customRange = null }) => {
  const [chartType, setChartType] = useState('bar');
  const [showGrid, setShowGrid] = useState(true);
  const [timeGrouping, setTimeGrouping] = useState('daily');

  console.log("Graphs component received apiData:", apiData);

  // Dynamic daysPerBar logic based on timeGrouping and daysCount
  let daysPerBar = 1;
  
  if (timeGrouping === 'daily') {
    daysPerBar = 1;
  } else if (timeGrouping === 'weekly') {
    daysPerBar = 7;
  } else if (timeGrouping === 'monthly') {
    daysPerBar = 30;
  } else {
    // Auto grouping based on total days
    if (daysCount > 15 && daysCount <= 30) daysPerBar = 2;
    else if (daysCount > 30 && daysCount <= 60) daysPerBar = 4;
    else if (daysCount > 60 && daysCount <= 120) daysPerBar = 7;
    else if (daysCount > 120) daysPerBar = 30;
  }
  
    const dateRange = useMemo(() => {
      const dates = [];
      const start = customRange?.fromDate ? new Date(customRange.fromDate) : null;
      const today = customRange?.toDate ? new Date(customRange.toDate) : new Date();
    
      if (!start) {
        // fallback: existing logic
        for (let i = 0; i < daysCount; i++) {
          const d = new Date(today);
          d.setDate(today.getDate() - i);
          dates.push({
            date: d,
            label: `${d.getDate()} ${d.toLocaleString('default', { month: 'short' })}`,
            key: d.toISOString().split('T')[0]
          });
        }
        return dates.reverse();
      }
    
      // new logic: from fromDate to toDate inclusive
      const current = new Date(start);
      while (current <= today) {
        const d = new Date(current);
        dates.push({
          date: d,
          label: `${d.getDate()} ${d.toLocaleString('default', { month: 'short' })}`,
          key: d.toISOString().split('T')[0]
        });
        current.setDate(current.getDate() + 1);
      }
      return dates;
    }, [customRange, daysCount]);
  

  const dateValueMap = useMemo(() => {
    const map = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    dateRange.forEach(({ key }) => (map[key] = 0));
    
    apiData.forEach(item => {
      try {
        const date = new Date(today);
        // date.setDate(today.getDate() - (item.day - 2));

        const yesterday = new Date(today);
        yesterday.setDate(today.getDate());
        yesterday.setHours(0, 0, 0, 0);

        if (customRange?.fromDate === yesterday.toISOString().split('T')[0] && customRange?.toDate === yesterday.toISOString().split('T')[0]) {
          console.log(yesterday.toISOString().split('T')[0], customRange?.fromDate, customRange?.toDate)
          // console.log("THIS is yeterdayyyyyyyyyyyyyyyyy")
          date.setDate(today.getDate() - (item.day - 1));
        }else if (daysCount == 1){
          console.log(yesterday.toISOString().split('T')[0], customRange?.fromDate, customRange?.toDate)
          // console.log("THIS is Todayyyyyyyyyyyyy")
          date.setDate(today.getDate() - (item.day - 2));
        }else {
          date.setDate(today.getDate() - (item.day - 1));
        }
        
        if (!isNaN(date.getTime())) {
          const key = date.toISOString().split('T')[0];
          if (map.hasOwnProperty(key)) map[key] = item.value;
        }
      } catch (error) {
        console.warn('Invalid date encountered in apiData:', item);
      }
    });
    
    return map;
  }, [apiData, dateRange]);

  const chartData = useMemo(() => {
    const grouped = [];
    for (let i = 0; i < dateRange.length; i += daysPerBar) {
      const chunk = dateRange.slice(i, i + daysPerBar);
      const sumValue = chunk.reduce((sum, { key }) => sum + (dateValueMap[key] || 0), 0);
      const avgValue = sumValue / chunk.length;
      
      let label;
      if (timeGrouping === 'daily' || daysPerBar === 1) {
        label = chunk[0].label;
      } else if (timeGrouping === 'weekly') {
        const startDate = chunk[0].date;
        const endDate = chunk[chunk.length - 1].date;
        const weekStart = startDate.getDate();
        const weekEnd = endDate.getDate();
        const month = startDate.toLocaleString('default', { month: 'short' });
        label = `Week ${weekStart}-${weekEnd} ${month}`;
      } else if (timeGrouping === 'monthly') {
        const month = chunk[0].date.toLocaleString('default', { month: 'long' });
        const year = chunk[0].date.getFullYear();
        label = `${month} ${year}`;
      } else {
        const startDay = chunk[0].date.getDate();
        const endDay = chunk[chunk.length - 1].date.getDate();
        const month = chunk[0].date.toLocaleString('default', { month: 'short' });
        label = `${startDay}-${endDay} ${month}`;
      }
      
      grouped.push({ 
        name: label, 
        value: timeGrouping === 'daily' ? sumValue : Math.round(avgValue), // Use average for weekly/monthly
        fullValue: sumValue,
        avgValue: Math.round(avgValue),
        period: timeGrouping
      });
    }
    return grouped;
  }, [dateRange, dateValueMap, daysPerBar, timeGrouping]);

  const yAxisLabels = {
    'Total vehicles (unique)': 'Number of Vehicles',
    'Total energy': 'Energy (kW)',
    'Total sessions': 'Number of Sessions',
    'Total hours': 'Hours'
  };
  const yLabel = yAxisLabels[selected] || selected || "Value";

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white text-ink p-3 border border-neutral-200 rounded-lg shadow-lg">
          <p className="font-medium">{label}</p>
          <p className="text-brand-700 font-medium">
            {timeGrouping === 'daily' 
              ? `${yLabel}: ${payload[0].value.toLocaleString()}`
              : `Average ${yLabel}: ${data.avgValue.toLocaleString()}`
            }
          </p>
          {timeGrouping !== 'daily' && (
            <p className="text-neutral-600 text-sm">
              Total: {data.fullValue.toLocaleString()}
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  // Sample data if no apiData provided
  const sampleData = useMemo(() => {
    if (apiData.length > 0) return [];
    return Array.from({ length: Math.min(daysCount, 30) }, (_, i) => ({
      day: i + 1,
      value: Math.floor(Math.random() * 100) + 20
    }));
  }, [apiData, daysCount]);

  const displayData = apiData.length > 0 
  ? chartData 
  : dateRange.map((d) => ({
      name: d.label,
      value: 0,
      fullValue: 0,
      avgValue: 0,
      period: timeGrouping
    }));


  const renderChart = () => {
    const commonProps = {
      data: displayData,
      margin: { top: 10, right: 10, left: 10, bottom: 40 }
    };

    const commonElements = (
      <>
        {showGrid && <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} opacity={1} />}
        <XAxis 
          dataKey="name" 
          angle={timeGrouping === 'monthly' || daysCount > 15 ? -45 : 0}
          textAnchor={timeGrouping === 'monthly' || daysCount > 15 ? 'end' : 'middle'}
          tick={{ fill: chartColors.axis, fontSize: window.innerWidth < 768 ? 10 : 12 }}
          interval={window.innerWidth < 768 ? 'preserveStartEnd' : timeGrouping === 'monthly' ? 0 : 'preserveEnd'}
          height={timeGrouping === 'monthly' || daysCount > 15 ? 80 : 60}
        />
        <YAxis 
          label={window.innerWidth >= 768 ? { 
            value: yLabel, 
            angle: -90, 
            position: 'insideLeft', 
            style: { textAnchor: 'middle', fill: chartColors.axis } 
          } : undefined}
          tick={{ fill: chartColors.axis, fontSize: window.innerWidth < 768 ? 10 : 12 }}
          width={window.innerWidth < 768 ? 50 : 80}
        />
        <Tooltip content={<CustomTooltip />} />
      </>
    );

    switch (chartType) {
      case 'line':
        return (
          <LineChart {...commonProps}>
            {commonElements}
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke="#70f63bff" 
              strokeWidth={window.innerWidth < 768 ? 2 : 3}
              dot={{ fill: chartColors.primary, strokeWidth: 2, r: window.innerWidth < 768 ? 3 : 4 }} 
              activeDot={{ r: window.innerWidth < 768 ? 4 : 6, fill: chartColors.primaryDark }} 
            />
          </LineChart>
        );
      case 'composed':
        return (
          <ComposedChart {...commonProps}>
            {commonElements}
            <Bar 
              dataKey="value" 
              fill={chartColors.primary} 
              radius={[2, 2, 0, 0]}
              barSize={window.innerWidth < 768 ? 15 : 20}
              opacity={0.7}
            />
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke="#1ff50bff" 
              strokeWidth={window.innerWidth < 768 ? 2 : 3}
              dot={false} 
              activeDot={{ r: window.innerWidth < 768 ? 4 : 6 }} 
            />
          </ComposedChart>
        );
      default:
        return (
          <BarChart {...commonProps}>
            {commonElements}
            <Bar 
              dataKey="value" 
              fill={chartColors.primary} 
              radius={[4, 4, 0, 0]}
              barSize={window.innerWidth < 768 ? 15 : 25}
            />
          </BarChart>
        );
    }
  };

  return (
    <div className="w-full max-w-full bg-white border border-neutral-200 rounded-xl shadow-card p-4 overflow-hidden">
      {/* Header with title and controls */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-3 sm:space-y-0">
        <h3 className="text-[15px] font-semibold tracking-tight text-brand-950 truncate">
          {selected || 'Chart Data'}
        </h3>
        
        {/* Controls */}
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {/* Time Grouping Filter */}
          <div className="flex bg-neutral-100 ring-1 ring-neutral-200 rounded-md overflow-hidden">
            <button
              onClick={() => setTimeGrouping('daily')}
              className={`px-3 py-1 text-xs transition-colors ${
                timeGrouping === 'daily' 
                  ? 'bg-brand-600 text-white font-medium' 
                  : 'text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              Daily
            </button>
            <button
              onClick={() => setTimeGrouping('weekly')}
              className={`px-3 py-1 text-xs transition-colors ${
                timeGrouping === 'weekly' 
                  ? 'bg-brand-600 text-white font-medium' 
                  : 'text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              Weekly
            </button>
            <button
              onClick={() => setTimeGrouping('monthly')}
              className={`px-3 py-1 text-xs transition-colors ${
                timeGrouping === 'monthly' 
                  ? 'bg-brand-600 text-white font-medium' 
                  : 'text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              Monthly
            </button>
          </div>

          {/* Chart Type Filter */}
          <div className="flex bg-neutral-100 ring-1 ring-neutral-200 rounded-md overflow-hidden">
            <button
              onClick={() => setChartType('bar')}
              className={`px-3 py-1 text-xs transition-colors ${
                chartType === 'bar' 
                  ? 'bg-brand-600 text-white font-medium' 
                  : 'text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              Bar
            </button>
            <button
              onClick={() => setChartType('line')}
              className={`px-3 py-1 text-xs transition-colors ${
                chartType === 'line' 
                  ? 'bg-brand-600 text-white font-medium' 
                  : 'text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              Line
            </button>
            <button
              onClick={() => setChartType('composed')}
              className={`px-3 py-1 text-xs transition-colors ${
                chartType === 'composed' 
                  ? 'bg-brand-600 text-white font-medium' 
                  : 'text-neutral-600 hover:bg-neutral-200'
              }`}
            >
              Mixed
            </button>
          </div>

          {/* Grid Toggle */}
          <button
            onClick={() => setShowGrid(!showGrid)}
            className={`px-3 py-1 text-xs rounded transition-colors ${
              showGrid 
                ? 'bg-brand-600 text-white font-medium' 
                : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
            }`}
          >
            Grid
          </button>

          {/* Days Filter */}
          {onDaysChange && (
            <select
              value={daysCount}
              onChange={(e) => onDaysChange(parseInt(e.target.value))}
              className="px-2 py-1 text-xs bg-white text-ink rounded border border-neutral-300 focus:outline-none focus:border-orange-500"
            >
              <option value={7}>7 days</option>
              <option value={15}>15 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
              <option value={120}>120 days</option>
            </select>
          )}
        </div>
      </div>

      {/* Chart Container */}
      <div className="w-full" style={{ height: window.innerWidth < 768 ? '250px' : '350px' }}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </div>

      {/* Mobile-friendly stats summary */}
      {window.innerWidth < 768 && displayData.length > 0 && (
        <div className="mt-3 pt-3 border-t border-neutral-200">
          <div className="grid grid-cols-3 gap-2 text-xs text-neutral-500">
            <div className="text-center">
              <div className="font-medium text-brand-600">
                {Math.max(...displayData.map(d => d.value)).toLocaleString()}
              </div>
              <div>Max</div>
            </div>
            <div className="text-center">
              <div className="font-medium text-green-600">
                {Math.round(displayData.reduce((sum, d) => sum + d.value, 0) / displayData.length).toLocaleString()}
              </div>
              <div>Avg</div>
            </div>
            <div className="text-center">
              <div className="font-medium text-yellow-400">
                {displayData.reduce((sum, d) => sum + d.value, 0).toLocaleString()}
              </div>
              <div>Total</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Graphs;